/**
 * The match viewer's postMessage bridge — the club match page hosted in an
 * iframe or a popped-out window, driven by the page that opened it.
 *
 *   host → viewer  { type: SEEK_TO_FRAME, frame }          seek the video
 *   viewer → host  { type: VIEWER_READY, video: boolean }  loaded; `video`
 *                                                          false when the
 *                                                          game has none
 *
 * A host holds its seeks until READY: a seek posted earlier lands in a
 * document whose listener or video is not there yet and is lost silently.
 * Same origin both ways; each side checks event.origin. Every host goes
 * through useViewerSeek, so the rule lives in one place.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export const SEEK_TO_FRAME = 'priori:seekToFrame'
export const VIEWER_READY = 'priori:viewerReady'

/** Tell whoever hosts this page (iframe parent, or the opener of a
 *  popped-out window) that it has loaded, and whether there is a video
 *  for seeks to land on. A no-op when nothing hosts it. */
export function announceViewerReady(hasVideo: boolean, win: Window = window): void {
  const host = win.parent !== win ? win.parent : win.opener as Window | null
  host?.postMessage({ type: VIEWER_READY, video: hasVideo }, win.location.origin)
}

export type ViewerSeek = {
  /** Seek the viewer; held until it is ready, and `open()`ed when there
   *  is no viewer to send to. */
  seek: (frame: number) => void
  /** The viewer is being replaced or closed: forget readiness and any
   *  held seek. Call before showing a new one. */
  reset: () => void
  /** What to tell the reader when a seek cannot land yet — the game
   *  has no video, or the viewer has not loaded in VIEWER_READY_TIMEOUT_MS
   *  — else null. The seek stays held either way and lands if the viewer
   *  comes good. */
  notice: string | null
}

/** How long a held seek waits for the viewer before the page says so.
 *  Generous: a cold match page loads its game and a presigned video URL. */
export const VIEWER_READY_TIMEOUT_MS = 20_000
export const NO_VIDEO_NOTICE =
  'No video is linked to this game, so there is nothing to jump to.'
export const NOT_LOADING_NOTICE =
  'The match viewer has not finished loading — the jump will happen when it does. '
  + 'Close and reopen the viewer if it stays blank.'

/** The host side. `target` is the viewer's window as things stand (null
 *  when none is showing); `open` shows one. Only a READY from that
 *  window counts — an old viewer or another page is ignored. */
export function useViewerSeek(target: () => Window | null, open: () => void): ViewerSeek {
  const ready = useRef(false)
  const pending = useRef<number | null>(null)
  const [noVideo, setNoVideo] = useState(false)
  const noVideoRef = useRef(false)
  const [stuck, setStuck] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  const clearTimer = () => { window.clearTimeout(timer.current); timer.current = undefined }
  useEffect(() => clearTimer, [])
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return
      const d = e.data as { type?: string; video?: boolean } | null
      if (d?.type !== VIEWER_READY) return
      const t = target()
      if (!t || e.source !== t) return
      ready.current = true
      clearTimer()
      setStuck(false)
      const hasVideo = d.video !== false
      noVideoRef.current = !hasVideo
      setNoVideo(!hasVideo)
      // No video yet: the seek stays held — the viewer announces again
      // when one is attached, and the seek lands then.
      if (!hasVideo) return
      const f = pending.current
      pending.current = null
      if (f != null) t.postMessage({ type: SEEK_TO_FRAME, frame: f }, window.location.origin)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [target])
  const reset = useCallback(() => {
    ready.current = false
    pending.current = null
    noVideoRef.current = false
    setNoVideo(false)
    clearTimer()
    setStuck(false)
  }, [])
  const seek = useCallback((frame: number) => {
    const t = target()
    if (t && ready.current && !noVideoRef.current) {
      t.postMessage({ type: SEEK_TO_FRAME, frame }, window.location.origin)
      return
    }
    if (!t) { reset(); open() }
    pending.current = frame
    // Held for a viewer still loading: if it never says ready, say so
    // rather than leave the click a silent no-op.
    if (!ready.current && timer.current === undefined) {
      timer.current = window.setTimeout(() => {
        timer.current = undefined
        if (ready.current || pending.current == null) return
        console.warn('[match viewer] no ready signal after',
                     VIEWER_READY_TIMEOUT_MS, 'ms; a seek is held')
        setStuck(true)
      }, VIEWER_READY_TIMEOUT_MS)
    }
  }, [target, open, reset])
  const notice = noVideo ? NO_VIDEO_NOTICE : stuck ? NOT_LOADING_NOTICE : null
  return { seek, reset, notice }
}
